<template>
  <div class="expos-container q-pa-md">
    <div class="section-container">
      <div class="row q-col-gutter-md">
        <q-banner v-if="success" class="col-12 app-banner app-banner--success" dense rounded>
          {{ successMessage }}
        </q-banner>
        <q-banner v-if="error" class="col-12 app-banner app-banner--error" dense rounded>
          {{ errorMessage }}
        </q-banner>
        <div v-for="exhibit in archiveExhibits" :key="exhibit.id" class="col-12 col-sm-6 col-md-4">
          <q-card class="expo-card">
            <q-img 
              loading="lazy" 
              class="expo-image" 
              height="250px" 
              spinner-size="82px" 
              spinner-color="primary"
              :src="homeImageUrls[exhibit.id]"
              @error="onImageError(exhibit.id)"
            >
              <template v-slot:error>
                <div class="default-image-container bg-grey-3">
                  <q-icon name="image" size="80px" color="grey-7" />
                  <div class="text-grey-7 text-caption q-mt-sm">Aucune image disponible</div>
                </div>
              </template>
              <div class="absolute-bottom text-subtitle2 text-center bg-white" style="color: #c0c0c1; font-weight: bold; font-size: 1.2rem;">
                {{ exhibit.name }}
              </div>
            </q-img>
            <q-card-actions align="center" class="q-pa-md">
              <q-btn color="deep-purple-10" label="Voir" icon="visibility" @click="navigate(exhibit.id)" />
              <q-btn-dropdown color="grey" icon="more_vert" flat>
                <q-list>
                  <q-item clickable v-close-popup @click="unarchiveExhibit(exhibit)">
                    <q-item-section avatar>
                      <q-icon name="unarchive" color="positive"/>
                    </q-item-section>
                    <q-item-section>Désarchiver</q-item-section>
                  </q-item>
                </q-list>
              </q-btn-dropdown>
            </q-card-actions>
            <q-inner-loading showing color="primary" v-if="loading" />
          </q-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";

export default {
  setup() {
    const router = useRouter();
    const archiveExhibits = ref([]);
    const loading = ref(false);
    const success = ref(false);
    const error = ref(false);
    const successMessage = ref('');
    const errorMessage = ref('');
    const homeImageUrls = ref({});

    const fetchArchiveExhibits = async () => {
      try {
        loading.value = true;
        const response = await axios.get(`${process.env.VUE_APP_BASEURL}/archive-exhibits`);
        archiveExhibits.value = response.data;
        await fetchHomeImages();
      } catch (error) {
        console.error("Erreur lors de la récupération des archives:", error);
        errorMessage.value = "Erreur lors de la récupération des archives";
        error.value = true;
      } finally {
        loading.value = false;
      }
    };

    const fetchHomeImages = async () => {
      try {
        for (const exhibit of archiveExhibits.value) {
          try {
            const response = await axios.get(`${process.env.VUE_APP_BASEURL}/exhibit_home/${exhibit.id}`);
            if (response.data && response.data.length > 0) {
              const imageResponse = await axios.get(
                `${process.env.VUE_APP_BASEURL}/s3Resource/${response.data[0].name}`
              );
              homeImageUrls.value[exhibit.id] = imageResponse.data;
            }
          } catch (error) {
            console.error(`Erreur lors de la récupération de l'image pour l'exposition ${exhibit.id}:`, error);
          }
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des images:", error);
      }
    };

    const onImageError = (exhibitId) => {
      homeImageUrls.value[exhibitId] = null;
    };

    const navigate = (id) => {
      router.push({ path: "/expo/" + id });
    };

    const unarchiveExhibit = async (exhibit) => {
      try {
        if (confirm('Êtes-vous sûr de vouloir désarchiver cette exposition ?')) {
          loading.value = true;
          await axios.delete(`${process.env.VUE_APP_BASEURL}/archive-exhibit/${exhibit.id}`);
          successMessage.value = "L'exposition a été désarchivée avec succès";
          success.value = true;
          setTimeout(() => {
            success.value = false;
          }, 4000);
          await fetchArchiveExhibits();
        }
      } catch (error) {
        console.error("Erreur lors du désarchivage:", error);
        errorMessage.value = "Erreur lors du désarchivage de l'exposition";
        error.value = true;
        setTimeout(() => {
          error.value = false;
        }, 4000);
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      fetchArchiveExhibits();
    });

    return {
      archiveExhibits,
      loading,
      success,
      error,
      successMessage,
      errorMessage,
      homeImageUrls,
      onImageError,
      navigate,
      unarchiveExhibit,
    };
  },
};
</script>

<style scoped>
.expos-container {
  padding: 20px;
}

.expo-card {
  transition: transform 0.2s;
  background-color: white;
  border: 1px solid #c0c0c1; 
}

.expo-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 15px rgba(44, 82, 44, 0.1); /* Ombre verte */
}

.expo-image {
  background-color: #f0f7f0; /* Fond vert pastel pour les images */
}

.default-image-container {
  height: 250px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #e3f3e3 !important; /* Vert pastel pour le conteneur d'image par défaut */
}

.banner {
  margin-bottom: 1rem;
}
</style>
